import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Visibility,
  AccessTime,
  Devices,
  Language,
  GetApp,
  Refresh,
  Timeline,
  Public,
  Group,
  Schedule,
} from '@mui/icons-material';

interface AnalyticsData {
  overview: {
    totalVisitors: number;
    uniqueVisitors: number;
    totalPageViews: number;
    bounceRate: number;
    avgSessionDuration: number;
    avgPagesPerSession: number;
    mobileVisitors: number;
    desktopVisitors: number;
    tabletVisitors: number;
    // New visitor behavior metrics
    totalVisits: number;
    avgVisitsPerVisitor: number;
    repeatVisitors: number;
    repeatVisitorRate: number;
  };
  sessions: Array<{
    session_id: string;
    ip_address_hash: string;
    browser_name: string;
    os_name: string;
    device_type: string;
    country_name: string;
    first_visit: string;
    total_page_views: number;
    session_duration: number;
  }>;
  pages: Array<{
    page_url: string;
    page_title: string;
    total_views: number;
    unique_visitors: number;
    avg_time_on_page: number;
    bounce_rate: number;
  }>;
  trafficSources: Array<{
    source: string;
    sessions: number;
    unique_visitors: number;
    avg_session_duration: number;
    bounce_rate: number;
  }>;
  geography: Array<{
    country_name: string;
    country_code: string;
    sessions: number;
    unique_visitors: number;
    avg_session_duration: number;
  }>;
  technology: {
    browsers: Array<{
      browser_name: string;
      sessions: number;
      percentage: number;
    }>;
    devices: Array<{
      device_type: string;
      sessions: number;
      percentage: number;
    }>;
    os: Array<{
      os_name: string;
      sessions: number;
      percentage: number;
    }>;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminAnalytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [timeRange, setTimeRange] = useState('7d');

  // Helper function to get the correct API base URL
  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const baseUrl = getApiBaseUrl();

      // Fetch all analytics data
      const endpoints = [
        '/admin/analytics/overview',
        '/admin/analytics/sessions',
        '/admin/analytics/pages',
        '/admin/analytics/traffic-sources',
        '/admin/analytics/geography',
        '/admin/analytics/technology'
      ];

      const promises = endpoints.map(endpoint =>
        fetch(`${baseUrl}${endpoint}?timeRange=${timeRange}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).then(res => {
          console.log(`Analytics API ${endpoint} status:`, res.status);
          if (!res.ok) {
            console.error(`Analytics API ${endpoint} failed:`, res.status, res.statusText);
            throw new Error(`Failed to fetch ${endpoint}: ${res.status} ${res.statusText}`);
          }
          return res.text().then(text => {
            console.log(`Analytics API ${endpoint} response:`, text);
            try {
              return JSON.parse(text);
            } catch (e) {
              console.error(`Analytics API ${endpoint} - Invalid JSON:`, text);
              throw new Error(`Invalid JSON response from ${endpoint}`);
            }
          });
        })
      );

      const [overview, sessions, pages, trafficSources, geography, technology] = await Promise.all(promises);

      setAnalyticsData({
        overview: {
          totalVisitors: overview.data.summary.total_sessions || 0,
          uniqueVisitors: overview.data.summary.unique_visitors || 0,
          totalPageViews: parseInt(overview.data.summary.total_page_views) || 0,
          bounceRate: parseFloat(overview.data.summary.bounce_rate) || 0,
          avgSessionDuration: parseFloat(overview.data.summary.avg_session_duration) || 0,
          avgPagesPerSession: parseFloat(overview.data.summary.avg_pages_per_session) || 0,
          mobileVisitors: overview.data.summary.mobile_visits || 0,
          desktopVisitors: overview.data.summary.desktop_visits || 0,
          tabletVisitors: overview.data.summary.tablet_visits || 0,
          // New visitor behavior metrics
          totalVisits: overview.data.visitorMetrics?.total_visits || 0,
          avgVisitsPerVisitor: parseFloat(overview.data.visitorMetrics?.avg_visits_per_visitor) || 0,
          repeatVisitors: overview.data.visitorMetrics?.repeat_visitors || 0,
          repeatVisitorRate: parseFloat(overview.data.visitorMetrics?.repeat_visitor_rate) || 0,
        },
        sessions: sessions.data || [],
        pages: pages.data || [],
        trafficSources: trafficSources.data || [],
        geography: geography.data?.countries || [],
        technology: {
          browsers: technology.data?.browsers || [],
          devices: technology.data?.devices || [],
          os: technology.data?.operatingSystems || []
        }
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/admin/analytics/export?format=${format}&timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `analytics-${timeRange}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={fetchAnalyticsData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!analyticsData) {
    return (
      <Alert severity="info">
        No analytics data available yet. Visit your site to start collecting data.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Visitor Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTimeRange('24h')}
            color={timeRange === '24h' ? 'primary' : 'inherit'}
          >
            24h
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTimeRange('7d')}
            color={timeRange === '7d' ? 'primary' : 'inherit'}
          >
            7 days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTimeRange('30d')}
            color={timeRange === '30d' ? 'primary' : 'inherit'}
          >
            30 days
          </Button>
          <IconButton onClick={fetchAnalyticsData} size="small">
            <Refresh />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => handleExport('csv')}
            size="small"
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Visitors
                </Typography>
                <Typography variant="h4">
                  {analyticsData.overview.totalVisitors.toLocaleString()}
                </Typography>
              </Box>
              <Group color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Page Views
                </Typography>
                <Typography variant="h4">
                  {analyticsData.overview.totalPageViews.toLocaleString()}
                </Typography>
              </Box>
              <Visibility color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Avg. Session Duration
                </Typography>
                <Typography variant="h4">
                  {formatDuration(analyticsData.overview.avgSessionDuration)}
                </Typography>
              </Box>
              <AccessTime color="primary" />
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Bounce Rate
                </Typography>
                <Typography variant="h4">
                  {analyticsData.overview.bounceRate?.toFixed(1) || '0.0'}%
                </Typography>
              </Box>
              <TrendingUp color="primary" />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Visitor Behavior Metrics */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group color="primary" />
          Visitor Behavior
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Visits
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.overview.totalVisits.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Individual sessions
                  </Typography>
                </Box>
                <Timeline color="primary" />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg. Visits per Visitor
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.overview.avgVisitsPerVisitor.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Return frequency
                  </Typography>
                </Box>
                <TrendingUp color="primary" />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Repeat Visitors
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.overview.repeatVisitors.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Visitors with 2+ visits
                  </Typography>
                </Box>
                <Group color="primary" />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Repeat Visitor Rate
                  </Typography>
                  <Typography variant="h4">
                    {analyticsData.overview.repeatVisitorRate?.toFixed(1) || '0.0'}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    % who return
                  </Typography>
                </Box>
                <TrendingUp color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs for detailed analytics */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="analytics tabs">
            <Tab label="Recent Sessions" icon={<Schedule />} iconPosition="start" />
            <Tab label="Popular Pages" icon={<Public />} iconPosition="start" />
            <Tab label="Traffic Sources" icon={<Timeline />} iconPosition="start" />
            <Tab label="Geography" icon={<Language />} iconPosition="start" />
            <Tab label="Technology" icon={<Devices />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Session</TableCell>
                  <TableCell>Browser/OS</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Page Views</TableCell>
                  <TableCell>Started</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.sessions.slice(0, 10).map((session) => (
                  <TableRow key={session.session_id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {session.session_id.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {session.browser_name} / {session.os_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={session.device_type} 
                        size="small" 
                        color={session.device_type === 'mobile' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{session.country_name || 'Unknown'}</TableCell>
                    <TableCell>{formatDuration(session.session_duration)}</TableCell>
                    <TableCell>{session.total_page_views}</TableCell>
                    <TableCell>{formatDate(session.first_visit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell>Unique Visitors</TableCell>
                  <TableCell>Avg. Time</TableCell>
                  <TableCell>Bounce Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.pages.map((page, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {page.page_title || 'Untitled Page'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {page.page_url}
                      </Typography>
                    </TableCell>
                    <TableCell>{page.total_views.toLocaleString()}</TableCell>
                    <TableCell>{page.unique_visitors.toLocaleString()}</TableCell>
                    <TableCell>{formatDuration(page.avg_time_on_page)}</TableCell>
                    <TableCell>{parseFloat(String(page.bounce_rate || '0')).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Source</TableCell>
                  <TableCell>Sessions</TableCell>
                  <TableCell>Unique Visitors</TableCell>
                  <TableCell>Avg. Duration</TableCell>
                  <TableCell>Bounce Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.trafficSources.map((source, index) => (
                  <TableRow key={index}>
                    <TableCell>{source.source}</TableCell>
                    <TableCell>{source.sessions.toLocaleString()}</TableCell>
                    <TableCell>{source.unique_visitors.toLocaleString()}</TableCell>
                    <TableCell>{formatDuration(source.avg_session_duration)}</TableCell>
                    <TableCell>{parseFloat(String(source.bounce_rate || '0')).toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell>Sessions</TableCell>
                  <TableCell>Unique Visitors</TableCell>
                  <TableCell>Avg. Duration</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyticsData.geography.map((geo, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{geo.country_code}</span>
                        {geo.country_name}
                      </Box>
                    </TableCell>
                    <TableCell>{geo.sessions.toLocaleString()}</TableCell>
                    <TableCell>{geo.unique_visitors.toLocaleString()}</TableCell>
                    <TableCell>{formatDuration(geo.avg_session_duration)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>Browsers</Typography>
              <List dense>
                {analyticsData.technology.browsers.map((browser, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={browser.browser_name} 
                      secondary={`${browser.sessions} sessions`}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {parseFloat(String(browser.percentage || '0')).toFixed(1)}%
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>Devices</Typography>
              <List dense>
                {analyticsData.technology.devices.map((device, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={device.device_type} 
                      secondary={`${device.sessions} sessions`}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {parseFloat(String(device.percentage || '0')).toFixed(1)}%
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>Operating Systems</Typography>
              <List dense>
                {analyticsData.technology.os.map((os, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={os.os_name} 
                      secondary={`${os.sessions} sessions`}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {parseFloat(String(os.percentage || '0')).toFixed(1)}%
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default AdminAnalytics;